import { Box, Flex, Group, Radio, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Period } from '../utils';
import SectionCard from './SectionCard';
import { SectionAddButton } from './SectionAddButton';

const ACCORDION_CHEVRON = <IconChevronDown size={20} stroke={1.5} />;

type CashflowSectionProps = {
  value: string;
  title: string;
  count: number;
  totalNode: ReactNode;
  period?: Period;
  onPeriodChange?: (period: Period) => void;
  showPeriodToggle?: boolean;
  onAdd?: () => void;
  children: ReactNode;
};

export default function CashflowSection({
  value,
  title,
  count,
  totalNode,
  period,
  onPeriodChange,
  showPeriodToggle = false,
  onAdd,
  children,
}: CashflowSectionProps) {
  const testId = title.toLowerCase().replace(/\s+/g, '-');
  const canTogglePeriod = showPeriodToggle && period && onPeriodChange;

  return (
    <SectionCard
      value={value}
      title={title}
      count={count}
      variant="cells"
      bg="white"
      chevron={ACCORDION_CHEVRON}
      style={{ borderRadius: 16, overflow: 'hidden' }}
      styles={{ control: { backgroundColor: 'var(--mantine-color-gray-0)' } }}
      controlTestId={`${testId}-control`}
      countTestId={`${testId}-count`}
      panelTestId={`${testId}-panel`}
      rightSection={
        <Group
          gap={24}
          wrap="nowrap"
          align="center"
          mr={48}
          py="sm"
          style={{ position: 'relative', pointerEvents: 'none' }}
        >
          <Flex direction="column" align="flex-end" justify="center" gap="xs">
            {canTogglePeriod && (
              <PeriodToggle
                testId={`${testId}-period`}
                period={period}
                onPeriodChange={onPeriodChange}
              />
            )}
            <Box style={{ pointerEvents: 'auto' }}>{totalNode}</Box>
          </Flex>
          {onAdd && (
            <Box style={{ pointerEvents: 'auto' }}>
              <SectionAddButton
                action={{ onAdd }}
                ariaLabel={`Add ${title}`}
                testId={`${testId}-add`}
              />
            </Box>
          )}
        </Group>
      }
    >
      {children}
    </SectionCard>
  );
}

type PeriodToggleProps = {
  testId: string;
  period: Period;
  onPeriodChange: (period: Period) => void;
};

function PeriodToggle({ testId, period, onPeriodChange }: PeriodToggleProps) {
  return (
    <Radio.Group
      value={period}
      onChange={value => onPeriodChange(value as Period)}
      data-testid={testId}
      style={{ pointerEvents: 'auto' }}
    >
      <Group gap="md" wrap="nowrap">
        <PeriodRadio value="monthly" label="Monthly" selected={period === 'monthly'} />
        <PeriodRadio value="annually" label="Annually" selected={period === 'annually'} />
      </Group>
    </Radio.Group>
  );
}

type PeriodRadioProps = {
  value: Period;
  label: string;
  selected: boolean;
};

function PeriodRadio({ value, label, selected }: PeriodRadioProps) {
  return (
    <Radio
      value={value}
      size="xs"
      color="brand.6"
      label={
        <Text fw={FONT_WEIGHTS.semibold} size="xs" c={selected ? 'brand.7' : 'gray.4'}>
          {label}
        </Text>
      }
      styles={{ radio: { borderColor: selected ? undefined : 'var(--mantine-color-gray-3)' } }}
    />
  );
}
